import { Avatar, Box, Button, Card, CardContent, Chip, FormControl, FormLabel, Grid2 as Grid, MenuItem, Select, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { memo } from 'react'
import { shallowEqual } from 'react-redux'

import { TimeSlotsBookingCard } from './TimeSlotsBookingCard'
import { SPORTS } from '../../share/Constant'
import { certificatesToSports } from '../../share/utility'
import { avatarUrl } from '../../utils/gravatar'
import { getSportProps } from '../../utils/utility'
import UserCard from '../cards/UserCard'
import { usePrivateSessionSearch } from '../hooks/usePrivateSessionSearch'

const SPORT_TYPES = Object.values(SPORTS).map((sport) => getSportProps(sport))

const InstructorMiniCard = memo(({ instructor, showCertificate = true, sx = {} }) => {
  let certificates = instructor.certificates
  if (!showCertificate) {
    certificates = []
  }

  return (
    <Grid
      container
      // direction="row"
      sx={{
        width: '100%',
        height: '100%',
        // display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        ...sx,
      }}
    >
      {/* Name and certificates */}
      <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Avatar
          src={avatarUrl(instructor)}
          sx={{
            bgcolor: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
          }}
        >
          {instructor.name?.charAt(0)}
        </Avatar>
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap align="left">
            {instructor.name}
          </Typography>
          <Grid sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {certificates.map((cert) => (
              <Chip
                key={cert}
                label={cert}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.75rem',
                  height: 20,
                  '& .MuiChip-label': {
                    px: 1,
                  },
                }}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
      {/* Sports */}
      <Grid
        sx={{
          ml: 'auto',
        }}
      >
        {certificatesToSports(instructor.certificates).map((sport) => (
          <Chip
            key={sport}
            label={sport === 'SNOWBOARD' ? 'Snowboarder' : 'Skier'}
            size="small"
            variant="filled"
            color={sport === 'SNOWBOARD' ? 'primary' : 'secondary'}
            sx={{
              borderRadius: '5px',
              height: 20,
              '& .MuiChip-label': {
                px: 1,
                fontSize: '0.75rem',
              },
              bgcolor: (theme) => (sport === 'SNOWBOARD' ? theme.palette.primary.main : theme.palette.secondary.light),
            }}
          />
        ))}
      </Grid>
    </Grid>
  )
}, shallowEqual)

/**
 * 教练预订组件 - 重构版本
 * 使用自定义Hook分离逻辑与UI
 */
export default function PrivateSessionSearch() {
  const {
    // 教练选择相关
    location,
    sportType,
    selectedInstructorId,
    availableLocations,
    handleLocationChange,
    handleSportTypeChange,
    handleInstructorChange,

    availableInstructorsDetail,
    availableSessions,
    instructorDetail,
  } = usePrivateSessionSearch()

  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Book a Private Lesson
      </Typography>

      <Grid container justifyContent="center" spacing={2}>
        {/* 左侧 - 预订选项 (地点、运动类型、教练选择) */}
        <Grid size={{ xs: 12, sm: 12, md: 7 }} maxWidth={600}>
          <Card>
            <CardContent>
              {/* 地点选择 */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.15rem',
                  }}
                >
                  Ski Resort
                </FormLabel>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {availableLocations.map((loc) => (
                    <Button
                      key={loc.value}
                      variant={location === loc.value ? 'contained' : 'outlined'}
                      onClick={() => handleLocationChange(loc.value)}
                      startIcon={loc.icon}
                      sx={{
                        borderRadius: 8,
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        fontSize: '0.875rem',
                        borderWidth: 1,
                        '& .MuiButton-startIcon': {
                          mr: 0.5,
                        },
                      }}
                    >
                      {loc.label}
                    </Button>
                  ))}
                </Stack>
              </FormControl>

              {/* 运动类型选择 */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.15rem',
                  }}
                >
                  Sport Type
                </FormLabel>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {SPORT_TYPES.map((type) => (
                    <Button
                      key={type.value}
                      variant={sportType === type.value ? 'contained' : 'outlined'}
                      onClick={() => handleSportTypeChange(type.value)}
                      startIcon={type.icon}
                      sx={{
                        borderRadius: 8,
                        textTransform: 'none',
                        px: 2,
                        py: 1,
                        fontSize: '0.875rem',
                        borderWidth: 1,
                        '& .MuiButton-startIcon': {
                          mr: 0.5,
                        },
                      }}
                    >
                      {type.label}
                    </Button>
                  ))}
                </Stack>
              </FormControl>

              {/* 教练选择 */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    letterSpacing: '0.15rem',
                  }}
                >
                  Instructor
                </FormLabel>
                <Select
                  value={selectedInstructorId}
                  onChange={(e) => handleInstructorChange(e.target.value)}
                  displayEmpty
                  sx={{ width: '100%', minHeight: 80 }}
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          {availableInstructorsDetail.length} {sportType ? (sportType === 'SNOWBOARD' ? 'snowboard' : 'ski') : ''} instructors available
                        </Typography>
                      )
                    }
                    const selectedInstructor = availableInstructorsDetail.find((inst) => inst.resourceId === value)
                    if (!selectedInstructor) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Select an instructor
                        </Typography>
                      )
                    }
                    return <InstructorMiniCard instructor={selectedInstructor} showCertificate={false} sx={{ pl: 2 }} />
                  }}
                >
                  <MenuItem value="" disabled sx={{ width: '100%', height: 'auto', py: 2 }}>
                    {availableInstructorsDetail.length} {sportType ? (sportType === 'SNOWBOARD' ? 'snowboard' : 'ski') : ''} instructors available
                  </MenuItem>
                  {availableInstructorsDetail.map((inst) => (
                    <MenuItem key={inst.resourceId} value={inst.resourceId} sx={{ maxWidth: '800px', width: '100%', height: 'auto', py: 2 }}>
                      <InstructorMiniCard key={inst.resourceId} instructor={inst} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 在大屏幕显示教练卡片 */}
              {instructorDetail && (
                <Box sx={{ mt: 2 }}>
                  {isLargeScreen ? <UserCard user={instructorDetail} size="small" /> : <UserCard user={instructorDetail} bio={false} size="small" sx={{ '&:hover': {} }} />}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 右侧 - 日期和时间选择 */}
        <Grid size={{ xs: 12, sm: 12, md: 5 }} maxWidth={600}>
          <TimeSlotsBookingCard availableSessions={availableSessions} instructorDetail={instructorDetail} />
        </Grid>
      </Grid>
    </Box>
  )
}
